import React, { Dispatch, SetStateAction, useMemo, useRef } from "react"
import { Dialog, DialogContent } from "../common/dialog"
import useTranslator from "@pma/hooks/use-translator"
import { cn } from "@pma/utils/helpers"
import { Button } from "../common/button"
import { useReactToPrint } from "react-to-print"
import { Printer } from "lucide-react"
import { Separator } from "../common/separator"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../common/table"
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table"
import { DataTableColumnHeader } from "../common/data-table/data-table-column-header"
import { formatCurrency } from "@pma/utils/utils"

interface EstimateBillOfQuantitiesPrintProps {
  data: any[]
  estimateBillOfQuantitiesPrintDialog: boolean
  setEstimateBillOfQuantitiesPrintDialog: Dispatch<SetStateAction<boolean>>
}

const EstimateBillOfQuantitiesPrint = ({
  data,
  estimateBillOfQuantitiesPrintDialog,
  setEstimateBillOfQuantitiesPrintDialog,
}: EstimateBillOfQuantitiesPrintProps) => {
  const billRef = useRef<HTMLDivElement>(null)

  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"

  const printBill = useReactToPrint({ contentRef: billRef, documentTitle: "ddd" })

  const columns = useMemo((): ColumnDef<any>[] => {
    const baseColumns: ColumnDef<any>[] = [
      {
        accessorKey: "number",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Number')} className='' />
        ),
        cell: ({ row }: any) => {
          const itemNumber = row?.original?.number
          return (
            <div className='flex gap-1'>
              <span className='text-xs sm:text-sm'>{itemNumber}</span>
            </div>
          )
        },
        enableSorting: false,
        size: 100,
      },
      {
        accessorKey: "description",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Description')} className='' />
        ),
        cell: ({ row }: any) => {
          const itemDescription = row?.original?.description
          return (
            <div className='max-w-[200px] text-right text-xs leading-tight sm:max-w-xs sm:text-sm lg:min-w-[370px]'>
              {itemDescription}
            </div>
          )
        },
        size: 300,
        enableSorting: false,
      },
      {
        accessorKey: "units",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Unit')} className='text-center' />
        ),
        cell: ({ row }: any) => {
          const itemUnit = row?.original?.units
          return <div className='text-center text-xs sm:text-sm'>{itemUnit}</div>
        },
        enableSorting: false,
      },
      {
        accessorKey: "amount",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Amount')} className='' />
        ),
        cell: ({ row }: any) => {
          const itemAmount = row?.original?.amount
          return <div className='text-center text-xs sm:text-sm'>{itemAmount}</div>
        },
        size: 150,
        enableSorting: false,
      },
      {
        accessorKey: "price",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Price')} className='' />
        ),
        cell: ({ row }: any) => {
          const itemPrice = row?.original?.price
          return (
            <div className='text-center font-mono text-xs sm:text-sm'>
              {itemPrice ? formatCurrency(itemPrice, isHebrew) : ""}
            </div>
          )
        },
        size: 100,
        enableSorting: false,
      },
      {
        accessorKey: "sum",
        header: ({ column }) => <DataTableColumnHeader column={column} title={translate('Sum')} className='' />,
        cell: ({ row }: any) => {
          const itemSum = row?.original?.sum
          return (
            <div className='text-center text-xs font-bold sm:text-sm'>
              {formatCurrency(itemSum, isHebrew)}
            </div>
          )
        },
        size: 50,
        enableSorting: false,
      },
    ]
    return baseColumns
  }, [isHebrew])

  const table = useReactTable({ data, columns, getCoreRowModel: getCoreRowModel() })

  return (
    <Dialog
      open={estimateBillOfQuantitiesPrintDialog}
      onOpenChange={setEstimateBillOfQuantitiesPrintDialog}
    >
      <DialogContent
        className={cn(
          "flex h-full max-h-[95vh] w-full flex-col overflow-y-hidden md:max-w-2xl lg:max-w-3xl xl:max-w-7xl",
          isHebrew ? "rtl" : "ltr",
        )}
      >
        <Button variant={"default"} onClick={printBill} className='h-8 w-[100px]'>
          <Printer className='mx-1 h-4 w-4' />
          {translate("Print")}
        </Button>
        <Separator />

        <style>
          {`
          @media print {
            @page {
              size: landscape;
            }
          }
        `}
        </style>
        <div className='flex-1 overflow-y-auto' ref={billRef}>
          <div className='w-full bg-gray-50 px-2 py-4 sm:px-4 md:py-8'>
            <div
              id={`ddd`}
              className='w-full rounded-lg bg-white p-4 shadow-lg sm:p-6 md:p-8 print:p-0 print:shadow-none'
              dir={isHebrew ? "rtl" : "ltr"}
            >
              <div className='mb-6 flex flex-col items-start justify-between gap-4 border-b-2 border-gray-200 pb-4 sm:flex-row'>
                <div className='flex w-full items-center gap-3 sm:w-auto'>
                  <div className='min-w-0'>
                    <h1 className='mb-1 text-xl font-bold text-blue-600 sm:text-2xl md:text-3xl'>
                      {translate("Estimate Items")}
                    </h1>
                  </div>
                </div>
              </div>
              <div className='overflow-x-auto rounded-md border'>
                <Table className='text-xs sm:text-sm'>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id} className='border-b bg-gray-50'>
                        {headerGroup.headers.map((header) => (
                          <TableHead
                            key={header.id}
                            className={cn(
                              "border-gray-200 px-1 py-2 text-center text-gray-900 sm:px-2 sm:py-3",
                              isHebrew ? "border-r first:border-r-0" : "border-l first:border-l-0",
                            )}
                            style={{ width: header.getSize() }}
                          >
                            {header.isPlaceholder ? null : (
                              <div className='flex items-center justify-center'>
                                {flexRender(header.column.columnDef.header, header.getContext())}
                              </div>
                            )}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows.map((row, index) => {
                      const rowData = row.original
                      const isParent = rowData.hasChildren
                      return (
                        <TableRow
                          key={row.id}
                          className={cn(
                            `border-b hover:bg-gray-50 ${
                              index % 2 === 0 ? "bg-white" : "bg-gray-25"
                            } ${isParent ? "font-medium" : ""}`,
                            rowData?.bill?.amount && "border-y border-primary bg-primary/5",
                          )}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell
                              key={cell.id}
                              className={cn(
                                "border-gray-200 px-1 py-1.5 sm:px-2 sm:py-2",
                                isHebrew
                                  ? "border-r first:border-r-0"
                                  : "border-l first:border-l-0",
                              )}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default EstimateBillOfQuantitiesPrint
